import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  AdminPanelSettings,
  Person,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../contexts/ClerkAuthContext';
import JuntoApiService from '../services/api';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

interface Admin {
  id: string;
  email: string;
  name: string;
  clerk_uid?: string;
  role: 'super_admin' | 'admin' | 'moderator';
  permissions: string[];
  is_active: boolean;
  created_at: string;
  last_login?: string;
  created_by?: string;
  notes?: string;
}

export default function AdminsPage() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'admin' as Admin['role'],
    notes: '',
  });
  const { user } = useAuth();

  const loadAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const data = await JuntoApiService.getAdmins();
      // TODO: Fix type mismatch between API response and local Admin interface
      setAdmins(data as any);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load admins:', err);
      setError('Failed to load admins. Make sure you have admin privileges.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const handleOpenDialog = (admin?: Admin) => {
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        email: admin.email,
        name: admin.name,
        role: admin.role,
        notes: admin.notes || '',
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        email: '',
        name: '',
        role: 'admin',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAdmin(null);
    setFormData({
      email: '',
      name: '',
      role: 'admin',
      notes: '',
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingAdmin) {
        // Update existing admin
        // TODO: API updateAdmin expects different structure
        await JuntoApiService.updateAdmin(editingAdmin.id, formData as any);
      } else {
        // Create new admin - API only needs user_id
        // TODO: Update this when API supports email-based admin creation
        await JuntoApiService.createAdmin({
          user_id: formData.email // Using email as user_id for now
        });
      }
      
      handleCloseDialog();
      loadAdmins();
    } catch (err: any) {
      console.error('Failed to save admin:', err);
      setError(err.response?.data?.detail || 'Failed to save admin');
    }
  };


  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <AdminPanelSettings color="primary" />;
      case 'admin':
        return <AdminPanelSettings />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role: string): "primary" | "secondary" | "default" => {
    switch (role) {
      case 'super_admin':
        return 'primary';
      case 'admin':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Admin Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Admin
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getRoleIcon(admin.role)}
                    <Typography variant="body2">{admin.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{admin.email}</Typography>
                  {admin.email === user?.email && (
                    <Chip label="You" size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={admin.role.replace('_', ' ').toUpperCase()} 
                    size="small"
                    color={getRoleColor(admin.role)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    icon={admin.is_active ? <CheckCircle /> : <Cancel />}
                    label={admin.is_active ? 'Active' : 'Inactive'}
                    size="small"
                    color={admin.is_active ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(admin.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {admin.last_login 
                      ? new Date(admin.last_login).toLocaleString()
                      : 'Never'
                    }
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <EntityActionButtons
                    entity={admin}
                    entityType="admins"
                    entityDisplayName="Admin"
                    onEdit={handleOpenDialog}
                    onRefresh={loadAdmins}
                    canEdit={admin.email !== 'kunalg@thesolroom.com'}
                    canDeactivate={admin.email !== user?.email}
                    canReactivate={admin.email !== user?.email}
                    renderEntityInHistory={(entity) => (
                      <Box>
                        <Typography variant="body1">
                          <strong>Name:</strong> {entity.name}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Email:</strong> {entity.email}
                        </Typography>
                        <Typography variant="body1">
                          <strong>Role:</strong> {entity.role?.replace('_', ' ').toUpperCase()}
                        </Typography>
                        {entity.clerk_uid && (
                          <Typography variant="body1">
                            <strong>Clerk UID:</strong> {entity.clerk_uid}
                          </Typography>
                        )}
                        {entity.notes && (
                          <Typography variant="body1">
                            <strong>Notes:</strong> {entity.notes}
                          </Typography>
                        )}
                        <Typography variant="body1">
                          <strong>Active:</strong> {entity.is_active ? 'Yes' : 'No'}
                        </Typography>
                      </Box>
                    )}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAdmin ? 'Edit Admin' : 'Add New Admin'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={!!editingAdmin}
                helperText={!editingAdmin ? "User must sign in with this email to activate their admin account" : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as Admin['role'] })}
                  label="Role"
                >
                  <MenuItem value="moderator">Moderator</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
                <FormHelperText>
                  Super Admins can manage other admins
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!formData.email || !formData.name}
          >
            {editingAdmin ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}